import type { EpisodeSummary } from "@scrile/contracts";
import { t } from "../i18n";
import { episodeState, type EpisodeState } from "../lib/episodes";
import { formatClock, formatMinutes, formatSchedule, formatShortDate } from "../lib/format";
import { episodeKey, loadPosition, progressShare } from "../lib/positions";
import { Icon } from "./Icon";

interface Props {
  episodes: readonly EpisodeSummary[];
  heroSlug: string;
  /** Серия в плеере — подсвечена. */
  currentOrdinal?: number | null;
  playing?: boolean;
  /** Часовой пояс подписчика: серия выходит по его часам; гостю — пояс устройства. */
  timeZone?: string | undefined;
  onSelect: (episode: EpisodeSummary, state: EpisodeState) => void;
}

/**
 * Список серий сезона с состоянием каждой: бесплатная, доступна, закрыта подпиской,
 * ещё не вышла. Закрытая нажимается (ведёт на пейвол), невышедшая — нет.
 */
export function EpisodeList({ episodes, heroSlug, currentOrdinal = null, playing = false, timeZone, onSelect }: Props) {
  return (
    <ol className="episodes">
      {episodes.map((episode) => {
        const state = episodeState(episode);
        const saved = loadPosition(episodeKey(heroSlug, episode.ordinal));
        const share = state === "free" || state === "available" ? progressShare(saved) : 0;
        const isCurrent = currentOrdinal === episode.ordinal;
        const finished = saved?.finished === true;
        const leftSec = saved && !finished ? Math.max(0, saved.durationSec - saved.positionSec) : 0;
        const disabled = state === "upcoming";

        const meta: string[] = [];
        if (state === "upcoming") {
          // Срок известен подписчику; без подписки (или пока расписания нет) — «Coming soon»
          meta.push(
            episode.scheduledAt
              ? t("episode.scheduled", { date: formatSchedule(episode.scheduledAt, timeZone) })
              : t("episode.upcoming"),
          );
        } else {
          meta.push(formatMinutes(episode.lengthSec));
          if (share > 0 && !finished && leftSec > 0) meta.push(t("episode.left", { time: formatClock(leftSec) }));
          else if (episode.releasedAt) meta.push(t("episode.out", { date: formatShortDate(episode.releasedAt) }));
        }

        return (
          <li key={episode.ordinal}>
            <button
              type="button"
              className={`episode episode--${state}${isCurrent ? " episode--current" : ""}`}
              onClick={() => onSelect(episode, state)}
              disabled={disabled}
              aria-current={isCurrent ? "true" : undefined}
            >
              <span className="episode__index" aria-hidden="true">
                {isCurrent && playing ? (
                  <span className="eq">
                    <i />
                    <i />
                    <i />
                  </span>
                ) : state === "locked" ? (
                  <Icon name="lock" size={18} />
                ) : finished ? (
                  <Icon name="check" size={18} />
                ) : (
                  String(episode.ordinal).padStart(2, "0")
                )}
              </span>
              <span className="episode__body">
                <span className="episode__title">{episode.title}</span>
                <span className="episode__meta">{meta.join(" · ")}</span>
                {share > 0 && share < 1 && (
                  <span className="episode__progress" aria-hidden="true">
                    <span style={{ width: `${Math.round(share * 100)}%` }} />
                  </span>
                )}
              </span>
              <span className="episode__badge">
                <Badge state={state} current={isCurrent && playing} finished={finished} />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function Badge({ state, current, finished }: { state: EpisodeState; current: boolean; finished: boolean }) {
  if (current) return <span className="badge badge--accent">{t("episode.playing")}</span>;
  if (finished && (state === "free" || state === "available")) return <span className="badge badge--quiet">{t("episode.played")}</span>;
  switch (state) {
    case "free":
      return <span className="badge badge--accent">{t("episode.free")}</span>;
    case "locked":
      return <span className="badge badge--quiet">{t("episode.locked")}</span>;
    case "upcoming":
      return <Icon name="clock" size={18} className="muted" />;
    case "available":
      return <Icon name="play" size={18} className="accent" />;
  }
}
